import {useDispatch, useSelector} from "react-redux";
import {useEffect, useState} from "react";
import {APPROVAL_TRACKER_MODEL} from "../Model/Model";
import {useTranslation} from "react-i18next";
import {useLocation, useNavigate, useParams} from "react-router-dom";
import {
    GetApprovalTrackerProgress,
    UpdateApprovalTrackerFlow
} from "../../../../reduxToolkit/ApprovalTracker/ApprovalTrackerApi";
import {errorMsg} from "../../../../Apis/functions";
import {STATUS_MODEL} from "../../../../enums/statusEnum";

/**
 * @Email : azapata@ibl.mx
 * @Author: Ferro Zapata Arnulfo
 * @jobTittle: Full Stack Developer
 *
 * ApprovalTrackerApp Hook
 *
 * Custom React hook that handles approval tracking logic, including loading tracker data,
 * controlling modal states (approve, deny, request changes), and managing related user interaction state.
 *
 * This hook is typically used in pages that need to display or manage approval flow progress for a specific module.
 *
 * @returns {{
 *   DEV: boolean,                                // Indicates whether the app is in development mode.
 *   permission: any,                             // User's permission object from authentication state.
 *   data: object,                                // Approval tracker form state (mirroring APPROVAL_TRACKER_MODEL).
 *   setData: Function,                           // State setter for the form data.
 *   t: Function,                                 // Translation function from react-i18next.
 *   genericCommentModals: Array<Object>,         // List of modal config objects for approval actions.
 *   setShowApprove: Function,                    // State setter to show/hide the approve comment modal.
 *   setShowDenied: Function,                     // State setter to show/hide the deny comment modal.
 *   setShowIssueComment: Function,               // State setter to show/hide the request changes comment modal.
 *   modified: boolean,                           // Tracks whether approval data has been updated.
 *   setModified: Function,                       // Sets the modified flag to trigger data reload.
 *   row: any,                                    // The selected row related to approval actions.
 *   setRow: Function,                            // Sets the selected row for modal interaction.
 *   getApprovalTrackerProgress: Function,        // Redux thunk action for fetching tracker progress data.
 *   user: object                                 // Authenticated user info from auth state.
 * }}
 *
 * */


export const ApprovalTrackerApp = ()=>{
    const { t } = useTranslation();
    const navigate = useNavigate();
    const {module,subjectId,approvalFlowId} = useParams()
    const dispatch = useDispatch(); // Redux dispatch function
    const location = useLocation();
    let { state } = location;
    const backUrl = state?.backUrl;
    const isOverdue = backUrl?.state?.isOverdue;
    const DEV = useSelector((state) => state?.CommonsSlice?.development);
    const { permission } = useSelector((state) => state.authenticatioauthennSlice);
    const { getApprovalTrackerProgress } = useSelector((state) => state.ApprovalTrackerSlice);
    const { user } = useSelector((state) => state.authenticatioauthennSlice);
    const [data, setData] = useState({ ...APPROVAL_TRACKER_MODEL });
    const [showApprove, setShowApprove] = useState(false);
    const [showDenied, setShowDenied] = useState(false);
    const [showIssueComment, setShowIssueComment] = useState(false);
    const [modified,setModified] = useState(false)
    const [row, setRow] = useState(null);
    const [showMsg, setShowMsg] = useState(false);

    const genericCommentModals =[
        {
            show: showApprove,
            setter: setShowApprove,
            title: 'approve_request',
            setRow: setRow,
            row:row,
            status: STATUS_MODEL.APPROVED,
            module :module,
            endPoint :UpdateApprovalTrackerFlow
        },
        {
            show: showDenied,
            setter: setShowDenied,
            title: 'decline_comment',
            setRow: setRow,
            row:row,
            status: STATUS_MODEL.DENIED,
            module :module,
            endPoint :UpdateApprovalTrackerFlow
        },
        {
            show: showIssueComment,
            setter: setShowIssueComment,
            title: 'request_changes',
            setRow: setRow,
            row:row,
            status: STATUS_MODEL.CHANGES_REQUESTED,
            module :module,
            endPoint :UpdateApprovalTrackerFlow
        },
    ];


    useEffect(() => {
        if (modified) {
            getData();
            setModified(false);
        }
    }, [modified]);

    // useEffect that runs every time `modified` value changes
    useEffect(() => {
        getData();
    }, []);

    const getData = () => {
        dispatch(GetApprovalTrackerProgress({
            approvalFlowId: approvalFlowId,
            subjectId: subjectId,
            module: module,
        })).then(
            ({ payload: { data: {success, code,data } } }) => {
                if (success === true) {
                    const isUserApprover = data?.levels?.some(level =>
                        level.approvers?.some(approver => approver.userId === user?.data?.id)
                    ) || false;
                    setShowMsg(isUserApprover);
                } else {
                    errorMsg(code);
                }
            }
        );
    };

    const handleOnBack = () => {
        navigate(
            backUrl.pathname, {
            state: backUrl.state
        });
    }

    return {
        DEV,
        permission,
        data,
        setData,
        t,
        genericCommentModals,
        setShowApprove,
        setShowDenied,
        setShowIssueComment,
        modified,
        setModified,
        row,
        setRow,
        getApprovalTrackerProgress,
        user,
        navigate,
        module,
        showMsg,
        isOverdue,
        handleOnBack
    }
}
